import { useAppQuery } from 'services/reactQuery/useAppQuery';
import AdminHTTPService from 'services/HTTPService/admin/AdminHTTPService';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { commonTexts, subscriptionsTexts } from 'consts/text';
import { SubscriptionType } from 'types';

type AdminInstitutionsPageNetworkManageProps = {
    search?: string;
    statuses?: SubscriptionType[];
};

export const useAdminInstitutionsPageNetworkManage = ({
    search,
    statuses,
}: AdminInstitutionsPageNetworkManageProps) => {
    const { displaySuccess, displayError } = useSimpleNotification();

    const {
        data: institutionSubscriptionDataItems,
        isInitialLoading: isInstitutionSubscriptionDataItemsInitialLoading,
        isRefetching: isInstitutionSubscriptionDataItemsRefetching,
        isError: isInstitutionSubscriptionDataItemsError,
    } = useAppQuery('INSTITUTION_SUBSCRIPTION_DATA_ITEMS', [search, statuses], () =>
        AdminHTTPService.clientSubscriptions.getInstitutionSubscriptionDataItems({
            search,
            statuses,
        }),
    );

    const handleBlockSubscriptionSuccess = () =>
        displaySuccess(subscriptionsTexts.list.actionMessages.successfullyBlockedSubscription);

    const handleBlockSubscriptionError = () =>
        displayError(commonTexts.errorMessages.actionExecution);

    const { mutate: blockSubscription, isLoading: isBlockSubscriptionLoading } = useAppMutation(
        AdminHTTPService.clientSubscriptions.blockSubscription,
        {
            key: ['BLOCK_SUBSCRIPTION'],
            onSuccess: handleBlockSubscriptionSuccess,
            onError: handleBlockSubscriptionError,
            invalidateQueryKeys: [['INSTITUTION_SUBSCRIPTION_DATA_ITEMS']],
        },
    );

    const handleVerifyInstitutionSuccess = () =>
        displaySuccess(subscriptionsTexts.list.actionMessages.successfullyVerifiedInstitution);

    const handleVerifyInstitutionError = () =>
        displayError(commonTexts.errorMessages.actionExecution);

    const { mutate: verifyInstitution, isLoading: isVerifyInstitutionLoading } = useAppMutation(
        AdminHTTPService.clientSubscriptions.verifyInstitution,
        {
            key: ['VERIFY_INSTITUTION'],
            onSuccess: handleVerifyInstitutionSuccess,
            onError: handleVerifyInstitutionError,
            invalidateQueryKeys: [['INSTITUTION_SUBSCRIPTION_DATA_ITEMS']],
        },
    );

    const handleDeleteInstitutionSuccess = () =>
        displaySuccess(subscriptionsTexts.list.actionMessages.successfullyDeletedInstitution);

    const handleDeleteInstitutionError = () =>
        displayError(commonTexts.errorMessages.actionExecution);

    const { mutate: deleteInstitution, isLoading: isDeleteInstitutionLoading } = useAppMutation(
        AdminHTTPService.clientSubscriptions.deleteInstitution,
        {
            key: ['DELETE_INSTITUTION'],
            onSuccess: handleDeleteInstitutionSuccess,
            onError: handleDeleteInstitutionError,
            invalidateQueryKeys: [['INSTITUTION_SUBSCRIPTION_DATA_ITEMS']],
        },
    );

    return {
        institutionSubscriptionDataItems,
        isInstitutionSubscriptionDataItemsInitialLoading,
        isInstitutionSubscriptionDataItemsRefetching,
        isBlockSubscriptionLoading,
        isVerifyInstitutionLoading,
        isDeleteInstitutionLoading,
        isInstitutionSubscriptionDataItemsError,
        blockSubscription,
        verifyInstitution,
        deleteInstitution,
    };
};
